import { inject, Injectable, OnDestroy } from "@angular/core";
import { ReadOnlyTokenStorage } from "@app/read-only-token.storage";
import { SessionUser } from "@domain/user/session-user";
import { environment } from "@environments/environment";
import { BusinessScope } from "@lib/constants/business-scope";
import { AuthConfig, OAuthService } from "angular-oauth2-oidc";
import { catchError, filter, from, map, Observable, ReplaySubject, Subject, takeUntil, throwError } from "rxjs";

export enum AuthErrorCode {
  WRONG_AUTH_LEVEL = "WRONG_AUTH_LEVEL",
  INVALID_LOGIN = "INVALID_LOGIN",
  OFFLINE = "OFFLINE",
}

export class AuthError extends Error {
  constructor(
    message: string,
    public code: AuthErrorCode,
  ) {
    super(message);
  }
}

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  private oauthService = inject(OAuthService);
  private readOnlyTokenStorage = inject(ReadOnlyTokenStorage);

  private static readonly QUERY_PARAM_READ_ACCESS_TOKEN = "readToken";
  private storedRedirectUri?: string;
  private readonly _destroying$ = new Subject<void>();
  private readonly _userInfo$ = new ReplaySubject<SessionUser>(1);

  private readonly AUTH_CONFIG: AuthConfig = {
    issuer: environment.oktaUri,
    redirectUri: window.location.origin,
    clientId: environment.oktaClientId,
    responseType: "code",
    scope: environment.useMyDraegerAuth ? environment.oktaScopes : environment.oktaScopesLegacy,
    showDebugInformation: !environment.production,
  };

  constructor() {
    let queryParams = new URLSearchParams();
    if (window.location.hash.includes("?")) {
      queryParams = new URLSearchParams(
        window.location.hash.substring(window.location.hash.indexOf("?"), window.location.hash.length),
      );
    }

    if (queryParams.has(AuthService.QUERY_PARAM_READ_ACCESS_TOKEN)) {
      window.location.href = this.initializeReadOnlyLink(queryParams);
      this._userInfo$.next({});
    } else if (this.readOnlyTokenStorage.get()) {
      this._userInfo$.next({});
    } else {
      this.readOnlyTokenStorage.clear();
      this.initDraegerLogin();
    }
  }

  get userInfo$(): Observable<SessionUser> {
    return this._userInfo$.asObservable();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  logout() {
    this.oauthService.logOut();
  }

  private initDraegerLogin() {
    this.storeRedirectUri();
    this.oauthService.configure(this.AUTH_CONFIG);
    this.oauthService.setupAutomaticSilentRefresh();

    from(this.oauthService.loadDiscoveryDocumentAndTryLogin())
      .pipe(
        catchError((error) => {
          console.error("Login error", error);
          return throwError(() => new AuthError("Login error: " + JSON.stringify(error), AuthErrorCode.INVALID_LOGIN));
        }),
        map(() => {
          if (this.oauthService.hasValidAccessToken()) {
            // Get claims from the ID token
            const identityClaims = this.oauthService.getIdentityClaims();

            // Decode access token to check for custom claims
            const accessToken = this.oauthService.getAccessToken();
            let accessTokenClaims: Record<string, any> = {};
            try {
              const payload = accessToken.split(".")[1];
              accessTokenClaims = JSON.parse(atob(payload));
            } catch (e) {
              console.error("Failed to decode access token:", e);
            }

            // Use access token claims if available, otherwise fall back to identity claims
            const claims = accessTokenClaims["rights"] ? accessTokenClaims : identityClaims;

            if (environment.useMyDraegerAuth) {
              return this.buildSessionUserFromMyDraegerClaims(claims);
            }
            return this.buildSessionUserFromLegacyClaims(claims);
          }

          if (!navigator.onLine) {
            throw new AuthError("Authorization not possible in offline mode", AuthErrorCode.OFFLINE);
          }

          this.oauthService.initLoginFlow(this.storedRedirectUri);
          return undefined;
        }),
        filter((userInfo) => !!userInfo),
        takeUntil(this._destroying$),
      )
      .subscribe({
        next: (userInfo) => {
          this._userInfo$.next(userInfo!);

          if (this.oauthService.state) {
            window.location.href = decodeURIComponent(this.oauthService.state);
          }
        },
        error: (error) => {
          this._userInfo$.error(error);
          this._userInfo$.complete();
        },
      });
  }

  private storeRedirectUri() {
    if (window.location.hash.split("#/").length > 1) {
      this.storedRedirectUri = window.location.href;
    }
  }

  private initializeReadOnlyLink(queryParams: URLSearchParams): string {
    const readToken = queryParams.get(AuthService.QUERY_PARAM_READ_ACCESS_TOKEN)!;
    this.readOnlyTokenStorage.set(readToken);
    const projectId = queryParams.get("projectId");
    if (projectId) {
      this.readOnlyTokenStorage.setProjectId(projectId, readToken);
    }
    const lang = queryParams.get("lang");
    if (lang) {
      this.readOnlyTokenStorage.setLanguage(lang);
    }
    queryParams.delete(AuthService.QUERY_PARAM_READ_ACCESS_TOKEN);
    return window.location.hash.substring(0, window.location.hash.indexOf("?") + 1) + queryParams.toString();
  }

  private buildSessionUserFromMyDraegerClaims(claims: Record<string, any>): SessionUser {
    const rights: string[] = claims["rights"] ?? [];
    return {
      id: claims["sub"],
      firstName: claims["firstname"],
      lastName: claims["lastname"],
      email: claims["email_address"] || claims["email"],
      rights,
      institutionId: claims["institution_id"],
      institutionType: claims["institution_type"],
      scope: this.resolveBusinessScopeFromRights(rights),
    };
  }

  /** @deprecated Remove after full migration to myDräger tokens */
  private buildSessionUserFromLegacyClaims(claims: Record<string, any>): SessionUser {
    this.validateAuthLevel(claims);
    const rawAuthLevel = claims["authLevel"];
    const authLevel = rawAuthLevel && rawAuthLevel !== "admin" ? JSON.parse(rawAuthLevel) : {};
    return {
      id: claims["sub"],
      firstName: claims["firstname"],
      lastName: claims["lastname"],
      email: claims["email_address"],
      authLevel,
      scope: this.resolveBusinessScopeFromAuthLevel(authLevel),
    };
  }

  private resolveBusinessScopeFromRights(rights: string[]): BusinessScope | undefined {
    if (rights.includes("N000")) return BusinessScope.FGDS;
    if (rights.includes("N001")) return BusinessScope.MONITORING;
    return undefined;
  }

  /** @deprecated Remove after full migration to myDräger tokens */
  private resolveBusinessScopeFromAuthLevel(authLevel: Record<string, string>): BusinessScope | undefined {
    if (Object.hasOwn(authLevel, "user/FGDS")) return BusinessScope.FGDS;
    if (Object.hasOwn(authLevel, "user/Monitoring")) return BusinessScope.MONITORING;
    return undefined;
  }

  /** @deprecated Remove after full migration to myDräger tokens */
  private validateAuthLevel(claims: Record<string, any>) {
    const authLevel = claims["authLevel"] || "admin";

    if (authLevel === "admin") return;

    try {
      const authLevelJson = JSON.parse(authLevel as string);
      if (authLevelJson.user !== "blocked") return;
    } catch {
      console.error("authLevel could not be parsed as json");
    }

    throw new AuthError("User is not permitted to use the app. Wrong auth level.", AuthErrorCode.WRONG_AUTH_LEVEL);
  }
}
